import { disposable, dispose, shouldDispose } from "./dispose.ts";
import { getTargetId, subscribe } from "./observable.ts";
import { reactTo } from "./react-to.ts";
import { type Property } from "./prop.ts";
import { evalFactor, type Factor } from "./utils.ts";

/** Event listener helper that disposes listeners properly */
export const on = (
    el : any, 
    eventNames : string, 
    handler : (event : Event) => any, options? : any
) => {
    for(const eventName of eventNames.split(" ")) {
        el.addEventListener(eventName, handler, options);
        shouldDispose(() => el.removeEventListener(eventName, handler));
    }
}

let hideClass = "";

/** Set configuration to hide elements using a class name instead of "display: none;" */
export const hideWithClass = (
    className : string
) => hideClass = className;

/** HTMLElement wrapper with reactive helper functions. */
export type RoleApi<El extends Element | null> = {
    readonly el : El; 

    /** Select a child of the current root element. */
    select: <ChildEl extends Element | null>(
        selector : string, 
        options? : { optional? : boolean; }
    ) => RoleApi<ChildEl>;

    /** Iterate over a selection of elements contained in current root element. */
    selectAll: <ChildEl extends Element | null>(
        selector : string, 
        child : (api : RoleApi<ChildEl>) => any
    ) =>  RoleApi<El>;

    /** Select an element by data-role. */
    role: <ChildEl extends Element | null>(
        roleName : string, 
        options? : { optional? : boolean; }
    ) => RoleApi<ChildEl>;

    /** Call a connector to delegate connecting. */
    connect: (
        connector: (api : RoleApi<El>) => any
    ) => RoleApi<El>;

    /** Connect a class */
    class: (
        className : string, 
        hasClass : Factor<any>
    ) => RoleApi<El>;

    /** Connect a style of the current element. */
    style: (
        styleName : string, 
        styleValue : Factor<string | null>
    ) => RoleApi<El>;

    /** Show/Hide the current element */
    show: (
        isShown : Factor<any>
    ) => RoleApi<El>;

    /** Connect an attribute of the current element. */
    attribute: (
        attributeName : string, 
        attributeValue : Factor<string | null>
    ) => RoleApi<El>;

    /** React to an attribute change. */
    onAttribute: (
        attributeName : string, 
        func : (value: string | null) => any
    ) => RoleApi<El>;

    /** Connect the value of the current element. */
    value: (
        value : Factor<any>
    ) => RoleApi<El>;

    /** Connect the text content of the current element. */
    text: (
        text : Factor<string>
    ) => RoleApi<El>;

    /** Connect the inner HTML of the current element. */
    html: (
        html: Factor<string>
    ) => RoleApi<El>;

    /** React to an event on the current element. */
    on: (
        eventNames : string, 
        handler : (event : Event) => any, 
        options? : any
    ) => RoleApi<El>;

    /** Connect a property to an element that allows input. */
    model: <Value>(
        p : Property<Value>
    ) => RoleApi<El>;

    /** Connect an iterated set items, creating child elements via template. */
    for: <ChildEl extends Element, T>(
        items : Factor<Array<T> | undefined>, 
        itemConnector? : (root : RoleApi<ChildEl>, context: T) => any
    ) => RoleApi<El>,
}

export const createApi = <El extends Element | null>(
    element : El = null as El
) : RoleApi<El> => {

    const api : RoleApi<El> = {
        get el() { return element; },

        select: <ChildEl extends Element | null>(
            selector : string, 
            { optional } : { optional? : boolean } = {}
        ) => {
            if(!api.el) return createApi();

            const element = api.el.querySelector(selector) as ChildEl;
            if(!element && !optional) {
                throw new Error(`Element not found for selector: "${selector}"`)
            }

            return createApi<ChildEl>(element);
        },

        selectAll: <ChildEl extends Element | null>(
            selector : string, 
            connect : (api : RoleApi<ChildEl>) => any
        ) => {
            if(!api.el) return createApi();

            const elements = [...api.el.querySelectorAll(selector)] as ChildEl[];

            elements.forEach((e) => connect(createApi<ChildEl>(e)));

            return api;
        },

        role: (
            roleName : string, 
            options?: { optional? : boolean; } 
        ) => api.select(`[data-role='${roleName}']`, options),

        connect: (
            connector: (api : RoleApi<El>) => any
        ) => {
            if(!api.el) return createApi();

            connector(api);
            return api; 
        },

        class: (
            className : string, 
            hasClass : Factor<any>
        ) => {
            if(api.el) {
                const el = api.el as Element;

                reactTo(hasClass).with(value => {
                    if(value) {
                        el.classList.add(className);
                    } else {
                        el.classList.remove(className);
                        if (el.classList.length === 0) {
                            el.removeAttribute('class');
                        }
                    }
                });
            }
            return api;
        },

        style: (
            styleName : string, 
            styleValue : Factor<string | null> 
        ) => {
            if(api.el) {
                reactTo(styleValue).with(value => (api.el as HTMLElement).style.setProperty(styleName, value ?? null));
            }
            return api;
        },

        show: (
            condition : () => Factor<any>
        ) => {
            if(hideClass) {
                return api.class(hideClass, () => !evalFactor(condition));
            } else {
                return api.style("display", () => evalFactor(condition) ? null : "none");
            }
        },

        attribute: (
            attributeName : string, 
            attributeValue? : Factor<string | null>
        ) => {
            if(api.el) {
                const el = api.el as Element;

                reactTo(attributeValue).with(value => {
                    if(value == null) {
                        el.removeAttribute(attributeName);
                    } else {
                        el.setAttribute(attributeName, value);
                    }
                });
            }
            return api;
        },

        onAttribute: (
            attributeName : string, 
            func : (value: string | null) => any
        ) => {
            if(api.el) {
                const f = () => func(api.el?.getAttribute(attributeName) ?? null);

                const observer = new MutationObserver(f);
                observer.observe(api.el, {attributes: true, attributeFilter: [attributeName]})
                shouldDispose(() => observer.disconnect());
                
                f();
            }
            return api;
        },

        value: (
            value: Factor<any>
        ) => {
            if(api.el) {
                reactTo(value).with(value => (api.el as HTMLInputElement).value = value);
            }
            return api;
        },

        text: (
            text: Factor<string>
        ) => {
            if(api.el) {
                reactTo(text).with(text => (api.el as Element).textContent = text ?? null);
            }
            return api;
        },

        html: (
            html: Factor<string>
        ) => {
            if(api.el) {
                reactTo(html).with(html => (api.el as Element).innerHTML = html ?? "");
            }
            return api;
        },

        on: (
            eventNames : string, 
            handler : (event : Event) => any, 
            options? : any
        ) => {
            if(api.el) {
                on(api.el as Element, eventNames, handler, options);
            }
            return api;
        },

        model: (
            prop : Property<any>
        ) => {
            if(api.el) {
                const el = api.el as HTMLInputElement;
                const type = el.getAttribute("type")?.toUpperCase();

                if(api.el.tagName === "INPUT" && type === "CHECKBOX") {
                    reactTo(prop).with(value => el.checked = !!value);
                    api.on("change", () => prop.value = el.checked);
                } else if(api.el.tagName === "INPUT" && type === "RADIO") {
                    api.attribute("checked", () => prop.value == el.getAttribute("value") ? "" : null)
                    api.on("change", () => prop.value = el.value);
                } else if((el.getAttribute("contenteditable") || "false").toLowerCase() !== "false" ) {
                    reactTo(prop).with(value => {
                        if(value != el.innerHTML) {
                            el.innerHTML = value?.toString() ?? ""; 
                        }
                    });
                    api.on("blur", () => prop.value = el.innerHTML);
                } else {
                    api.value(() => prop.value?.toString() ?? "");
                    api.on("change", () => prop.value = el.value);
                }
            }
            return api;
        },

        for: <ChildEl extends Element, T>(
            items : Factor<Array<T> | undefined>, 
            itemConnector : (root : RoleApi<ChildEl>, 
            context: T) => any = () => {}
        ) => {
            if(api.el) {
                const el = api.el as Element;
                const template =  [...(api.el.children)].filter(c => c.tagName === "TEMPLATE")[0] as HTMLTemplateElement;

                if(!template) {
                    throw new Error(`"for" requires a template.`);
                }

                const targetToElement = new Map();

                const disposer = () => {
                    while(el.childElementCount - 1 > 0) {
                        dispose(el.lastElementChild, "for, dispose all");
                        el.lastElementChild?.remove();
                    }
                    targetToElement.clear(); 
                }
                

                reactTo(items).withReset((items) => {
                    shouldDispose(disposer)

                    if(!items) {
                        disposer();
                        return;
                    }

                    const createChildComponent = (item : any) => {
                        const clone = template.content.cloneNode(true);
                        const div =  document.createElement("div");
                        div.appendChild(clone);

                        const element = div.lastElementChild as ChildEl;

                        const targetId = getTargetId(item);
                
                        if(targetId) {
                            targetToElement.set(targetId, element);
                        }
                
                        disposable(element, () => {
                            itemConnector(createApi(element), item);
                        });
                
                        return element;
                    }
                
                    for(const item of items) {
                        el.appendChild(createChildComponent(item));
                    }

                    shouldDispose(subscribe(items, "", () => {
                        let i = 0;
                        for(const item of items) {
                            let newElement = targetToElement.get(getTargetId(item));
                            if(!newElement) {
                                newElement = createChildComponent(item);
                            }

                            if(i >= el.children.length - 1) {
                                el.appendChild(newElement);
                            } else {
                                const oldElement = el.children[i + 1];
                                if(oldElement != newElement) {
                                    oldElement.replaceWith(newElement);
                                }
                            }
                            i++;
                        }
                        while(el.childElementCount - 1 > i) {
                            el.lastElementChild?.remove();
                        }
            
                        const extras = [...targetToElement].filter(e => ![...el.children].includes(e[1]));
            
                        for(const e of extras) {
                            dispose(e[1], "for");
                            targetToElement.delete(e[0]);
                        }

                    }));
                });
            }
            return api;
        }
    }
    return api;
};

/** Short-hand RoleApi type suitable for most cases */
export type Role = RoleApi<Element | null>;

export const { 
    /** Select an element by role. */
    role, 
    /** Select an element by selector. */
    select, 
    /** Select element by selector. */
    selectAll 
} = createApi(document as unknown as Element);



